import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck, ClipboardList, Send, Users, FileText, Activity,
  PlusCircle, Banknote, AlertTriangle, Mic, Clock, Heart, Moon, Sparkles, MousePointerClick,
} from "lucide-react";
import { toast } from "sonner";
import { api, auth } from "@/lib/api";
import { PageHeader, KpiCard, SectionCard } from "@/components/Primitives";
import OutcomeDashboard from "@/components/OutcomeDashboard";

export default function Dashboard() {
  const navigate = useNavigate();
  const user = auth.user();
  const [summary, setSummary] = useState(null);
  const [savings, setSavings] = useState(null);
  const [click, setClick] = useState(null);
  const [risk, setRisk] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/eod/savings").then((r) => setSavings(r.data)).catch(() => {});
    api.get("/ehr/click-burden").then((r) => setClick(r.data)).catch(() => {});
    api.get("/wellbeing/risk").then((r) => setRisk(r.data)).catch(() => {});
  }, []);

  const cards = [
    { label: "Open Prior Auths", value: summary?.open_prior_auths ?? "—", icon: ShieldCheck, hint: "Awaiting decision", testid: "kpi-open-pa" },
    { label: "Pending Claims", value: summary?.pending_claims ?? "—", icon: Banknote, hint: "Submitted, not paid", testid: "kpi-pending-claims" },
    { label: "Overdue Referrals", value: summary?.overdue_referrals ?? "—", icon: Send, hint: "No response / awaiting", testid: "kpi-overdue-refs" },
    { label: "Patients to Follow Up", value: summary?.patients_followup ?? "—", icon: Users, hint: "Care gaps", testid: "kpi-followup" },
    { label: "Credentials Expiring", value: summary?.credentials_expiring ?? "—", icon: AlertTriangle, hint: "Within 90 days", accent: true, testid: "kpi-cred-exp" },
    { label: "Doc Score Avg", value: `${summary?.documentation_score_avg ?? "—"}%`, icon: FileText, hint: "Liability shield", testid: "kpi-doc-score" },
  ];

  const quickActions = [
    { label: "ARIA Dictation", to: "/dictation", icon: Mic },
    { label: "Documentation Pro", to: "/documentation-pro", icon: FileText },
    { label: "Encounter Prep", to: "/encounter-prep", icon: ClipboardList },
    { label: "Time Saved", to: "/time-saved", icon: Clock },
  ];

  const wellbeingBandColor = risk?.band === "green"
    ? "text-emerald-700"
    : risk?.band === "yellow"
    ? "text-amber-700"
    : risk?.band === "red"
    ? "text-red-700"
    : "text-brand-muted";

  return (
    <>
      <PageHeader
        eyebrow="Command Center"
        title={`Welcome, ${user?.name?.split(" ")[1] ? "Dr. " + user.name.split(" ").slice(-1)[0] : user?.name}`}
        subtitle="Your operating system across charting, revenue, operations and engagement."
        actions={
          <button onClick={() => toast.info("Open audit log")} className="text-xs text-brand-muted hover:text-brand-primary" data-testid="audit-log-btn">
            View audit log
          </button>
        }
        testid="dashboard-header"
      />

      {/* Pain-Point Hero Strip — EOD button + Wellbeing + Click Burden + Time Saved + CamelionAI Bridge */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <button
          onClick={() => navigate("/bridge")}
          className="text-left rounded-xl p-5 bg-gradient-to-br from-emerald-600 to-brand-primary text-white hover:opacity-95 transition-opacity"
          data-testid="hero-bridge-btn"
        >
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-5 h-5" />
            <div className="text-[10px] uppercase tracking-wider opacity-80">CamelionAI Bridge</div>
          </div>
          <div className="text-xl font-semibold">8 connections live</div>
          <div className="text-xs opacity-80 mt-1">ZidaMD ⇄ ProsthoAI ⇄ ReimbursePro</div>
        </button>

        <button
          onClick={() => navigate("/eod")}
          className="text-left rounded-xl p-5 bg-gradient-to-br from-brand-primary to-[#5A1A9F] text-white hover:opacity-95 transition-opacity"
          data-testid="hero-eod-btn"
        >
          <div className="flex items-center gap-2 mb-2">
            <Moon className="w-5 h-5" />
            <div className="text-[10px] uppercase tracking-wider opacity-80">End of day</div>
          </div>
          <div className="text-xl font-semibold">Clear my queue</div>
          <div className="text-xs opacity-80 mt-1">Inbox zero in 15 minutes with ARIA.</div>
        </button>

        <button
          onClick={() => navigate("/wellbeing")}
          className="text-left rounded-xl p-5 border border-brand bg-white hover:bg-brand-bg/40 transition-colors"
          data-testid="hero-wellbeing-btn"
        >
          <div className="flex items-center gap-2 mb-2">
            <Heart className={`w-5 h-5 ${wellbeingBandColor}`} />
            <div className="text-[10px] uppercase tracking-wider text-brand-muted">Your wellbeing</div>
          </div>
          <div className={`text-xl font-semibold ${wellbeingBandColor}`}>
            {risk?.band === "unknown" || !risk ? "Start check-in" : risk.band === "green" ? "Good" : risk.band === "yellow" ? "Watch" : "Recover"}
          </div>
          <div className="text-xs text-brand-muted mt-1">{risk?.checkins_total ? `${risk.checkins_total} check-ins · private` : "Private to you only"}</div>
        </button>

        <button
          onClick={() => navigate("/time-saved")}
          className="text-left rounded-xl p-5 border border-brand bg-white hover:bg-brand-bg/40 transition-colors"
          data-testid="hero-timesaved-btn"
        >
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-brand-primary" />
            <div className="text-[10px] uppercase tracking-wider text-brand-muted">Hours this week</div>
          </div>
          <div className="text-xl font-semibold text-brand-text">{savings ? `${savings.weekly_hours}h saved` : "—"}</div>
          <div className="text-xs text-brand-muted mt-1">{savings ? `${savings.monthly_family_hours}h personal back this month` : "Track your ROI"}</div>
        </button>

        <div className="text-left rounded-xl p-5 border border-brand bg-white" data-testid="hero-click-burden">
          <div className="flex items-center gap-2 mb-2">
            <MousePointerClick className="w-5 h-5 text-brand-teal" />
            <div className="text-[10px] uppercase tracking-wider text-brand-muted">EHR clicks avoided</div>
          </div>
          <div className="text-xl font-semibold text-brand-text">{click ? click.clicks_avoided_estimate.toLocaleString() : "—"}</div>
          <div className="text-xs text-brand-muted mt-1">{click ? `${click.voice_commands_this_month} ARIA commands × ${click.click_per_command_ratio} clicks` : "—"}</div>
        </div>
      </div>

      {/* Burnout alert if applicable */}
      {risk?.alert && (
        <div className="border border-red-200 bg-red-50 rounded-lg p-3 mb-6 flex items-center gap-3" data-testid="dashboard-burnout-alert">
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
          <div className="flex-1 text-sm text-red-900">
            <b>ARIA noticed your wellbeing has trended down {risk.streak_declining}+ days.</b> Tap to see micro-interventions.
          </div>
          <button onClick={() => navigate("/wellbeing")} className="px-3 py-1.5 rounded-md bg-red-700 text-white text-xs font-medium hover:bg-red-800" data-testid="burnout-alert-cta">
            Open wellbeing
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        {cards.map((c) => <KpiCard key={c.label} {...c} />)}
      </div>

      <div className="mb-8">
        <OutcomeDashboard />
      </div>

      <SectionCard title="Quick Actions" testid="quick-actions">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          {quickActions.map((q) => (
            <button key={q.label} onClick={() => navigate(q.to)}
              className="flex items-center justify-between p-4 border border-brand rounded-lg hover:border-brand-secondary hover:bg-brand-bg transition-all text-left lift"
              data-testid={`qa-${q.label.toLowerCase().replace(/\s/g, "-")}`}>
              <div>
                <div className="text-sm font-medium text-brand-text">{q.label}</div>
                <div className="text-[11px] text-brand-muted mt-0.5">Start workflow</div>
              </div>
              <PlusCircle className="w-5 h-5 text-brand-primary" strokeWidth={1.5} />
            </button>
          ))}
        </div>
      </SectionCard>

      <div className="mt-8">
        <div className="eyebrow mb-3">Modules</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[
            { title: "CamelionAI Bridge", desc: "8 unified connections — ZidaMD ⇄ ProsthoAI ⇄ ReimbursePro. One clinical-financial ecosystem.", to: "/bridge" },
            { title: "End of Day Mode", desc: "Inbox zero. Lab follow-ups. Refills. All cleared in 15 minutes.", to: "/eod" },
            { title: "Wellbeing & Burnout Prevention", desc: "Private daily pulse, burnout-risk score, 2-minute micro-interventions.", to: "/wellbeing" },
            { title: "Documentation Pro", desc: "One sentence in. Complete SOAP out. Phrase library ARIA learns.", to: "/documentation-pro" },
            { title: "AI Charting Engine", desc: "Sign your day's notes by 5pm — SOAP drafts with CPT/ICD-10 already coded.", to: "/charting" },
            { title: "Prior Authorization AI", desc: "Stop chasing approvals — peer-reviewed PA letters drafted in under two minutes.", to: "/prior-auth" },
            { title: "Revenue Cycle Command", desc: "Catch denials before they age out — appeal-ready drafts for every CO-50 / CO-97.", to: "/revenue-cycle" },
            { title: "Liability Shield", desc: "Protect your license — consent forms and chart-defensibility scoring on every encounter.", to: "/liability" },
            { title: "Specialist Referral Hub", desc: "Close the loop on every referral — packet generation, scheduling and follow-up tracked end-to-end.", to: "/referrals" },
            { title: "Staffing AI Copilot", desc: "Never miss a credential expiry — automated rosters, task assignments and renewal alerts.", to: "/staffing" },
            { title: "EHR Bridge", desc: "Keep your existing EHR — read/write to Epic, Athena and eClinicalWorks without a rip-and-replace.", to: "/ehr-bridge" },
            { title: "Managed Care", desc: "Capture every HCC and HEDIS measure — risk-adjustment gaps surfaced at the point of care.", to: "/managed-care" },
            { title: "Patient Engagement", desc: "Improve adherence — plain-language after-visit instructions sent automatically.", to: "/patient-engagement" },
            { title: "Care Coordination", desc: "Cut 30-day readmissions — discharge summaries and TOC packets ready before the patient leaves.", to: "/care-coordination" },
            { title: "Hospital Operations", desc: "See the whole house — OR turnover, bed flow and credentialing on one dashboard.", to: "/hospital-ops" },
            { title: "Analytics", desc: "Know your numbers — revenue, denial and burnout trends in plain English, not pivot tables.", to: "/analytics" },
            { title: "PA & NP Collaboration", desc: "Supervise with confidence — co-sign queues and scope-of-practice guardrails built in.", to: "/pa-np-hub" },
            { title: "Voice Intelligence", desc: "Reclaim your evenings — ambient capture turns the conversation into a finished note.", to: "/voice-engine" },
            { title: "Predictive Intelligence", desc: "Catch critical patterns before they become emergencies — deterioration, denial and no-show alerts.", to: "/predictive" },
            { title: "Legal Intelligence", desc: "Defend your practice — early-warning scans, statute-of-limitations clocks and demand-letter responses.", to: "/legal-intelligence" },
            { title: "Pharma Manager", desc: "Stay Sunshine-Act compliant — automatic logging of every rep visit and formulary update.", to: "/pharma-manager" },
            { title: "CME & Licensing", desc: "Never miss a renewal — CME credits and licensure deadlines on a single calendar.", to: "/cme-tracker" },
            { title: "Peer Consult Network", desc: "Get a specialist second opinion in hours, not weeks — privacy-preserving curbside consults.", to: "/peer-consult" },
            { title: "Financial Wellness", desc: "Plan beyond the practice — contract analysis, retirement modeling and benchmarking.", to: "/financial-wellness" },
            { title: "Risk Stratification", desc: "Know your highest-risk panel members — proactive outreach lists, refreshed nightly.", to: "/risk-stratification" },
            { title: "Emergency Protocols", desc: "Activate the right protocol in one tap — anaphylaxis, stroke, sepsis, code blue checklists.", to: "/emergency-protocols" },
            { title: "Telehealth Intelligence", desc: "Bill telehealth correctly every time — state and payer rule lookup at the point of order.", to: "/telehealth" },
            { title: "Data Ownership", desc: "Your data, your asset — controlled export and de-identified trial matching opportunities.", to: "/data-intelligence" },
            { title: "Reputation Manager", desc: "Protect your online reputation — review monitoring and physician-appropriate response drafts.", to: "/reputation-manager" },
            { title: "Contract Intelligence", desc: "Stop being underpaid — payer-contract analysis with renegotiation talking points.", to: "/contract-intelligence" },
            { title: "Outcome Tracking", desc: "Prove your value — closed-loop outcomes across PA, CPT, ARIA and referrals in one view.", to: "/outcomes" },
          ].map((m) => (
            <button key={m.to} onClick={() => navigate(m.to)}
              className="text-left p-5 bg-white border border-brand rounded-xl lift"
              data-testid={`module-card-${m.to.replace("/", "")}`}>
              <Activity className="w-4 h-4 text-brand-teal mb-3" strokeWidth={1.6} />
              <div className="text-sm font-semibold text-brand-text">{m.title}</div>
              <div className="text-xs text-brand-muted mt-1">{m.desc}</div>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
